import React, { useEffect } from "react";
import { Button, Form, Input, Select, Space } from "antd";
import { useUpdateUser, UpdateUserRequest } from "~/services/user";
import { TUser } from "~/types";
import { Role } from "~/utils/constants";

const { Option } = Select;

interface EditUserFormProps {
  user: TUser;
  onSuccess: () => void;
  onCancel: () => void;
}

export const EditUserForm: React.FC<EditUserFormProps> = ({
  user,
  onSuccess,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const updateUserMutation = useUpdateUser();

  useEffect(() => {
    form.setFieldsValue({
      username: user.username,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      phoneNumber: user.phoneNumber,
      userRole: user.role,
    });
  }, [user, form]);

  const handleSubmit = async (values: UpdateUserRequest) => {
    try {
      await updateUserMutation.mutateAsync({
        id: user.id,
        userData: values,
      });
      onSuccess();
    } catch (error) {
      console.error("Update user error:", error);
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleSubmit}>
      <Form.Item
        name="username"
        label="Username"
        rules={[
          { required: true, message: "Please enter username" },
          { min: 3, message: "Username must be at least 3 characters" },
        ]}
      >
        <Input placeholder="Enter username" />
      </Form.Item>

      <Form.Item
        name="email"
        label="Email"
        rules={[
          { required: true, message: "Please enter email" },
          { type: "email", message: "Invalid email format" },
        ]}
      >
        <Input placeholder="Enter email" />
      </Form.Item>

      <Form.Item
        name="firstName"
        label="First Name"
        rules={[{ required: true, message: "Please enter first name" }]}
      >
        <Input placeholder="Enter first name" />
      </Form.Item>

      <Form.Item
        name="lastName"
        label="Last Name"
        rules={[{ required: true, message: "Please enter last name" }]}
      >
        <Input placeholder="Enter last name" />
      </Form.Item>

      <Form.Item name="phoneNumber" label="Phone Number">
        <Input placeholder="Enter phone number" />
      </Form.Item>

      <Form.Item
        name="userRole"
        label="Role"
        rules={[{ required: true, message: "Please select a role" }]}
      >
        <Select placeholder="Select role">
          <Option value={Role.SUPER_ADMIN}>Super Admin</Option>
          <Option value={Role.ADMIN}>Admin</Option>
          <Option value={Role.LEAD}>Lead</Option>
          <Option value={Role.STAFF}>Staff</Option>
          <Option value={Role.CLIENT}>Client</Option>
        </Select>
      </Form.Item>

      <Form.Item
        name="password"
        label="New Password (leave empty if no change)"
        rules={[{ min: 6, message: "Password must be at least 6 characters" }]}
      >
        <Input.Password placeholder="Enter new password" />
      </Form.Item>

      <Form.Item>
        <Space className="w-full justify-end">
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={updateUserMutation.isPending}
          >
            Update
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};
